import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { bool, func, string, shape, arrayOf, number, object } from "prop-types";
import { isEqual } from "lodash";
import { MAX_TEXT_DESCRIPTION_LENGTH } from "@repo/utils";
import Quill, { Delta } from "quill";
import { autoFormatUrls, formatUrlOnPaste, getQuillEditorToolPopupPosition, WYSIWYG_FORMATS } from "@utils";
import { useEstimateEditorStore } from "@tools";
import { PopMenu } from "@components";
import { ClickAwayListener, Grid } from "@material-ui/core";
import { QuillToolbar } from "@components/WysiwygEditor/components/QuillToolbar/QuillToolbar";
import classnames from "classnames";
import useStyle from "./DescriptionEditor.style";
import "quill/dist/quill.core.css";

export const DescriptionEditor = forwardRef(({
  focused,
  value: defaultValue,
  readOnly,
  onFocus,
  onBlur,
  onTextChange,
  placeholder,
  className,
  hasError,
  changeDelay = 120
}, ref ) => {
  const classes = useStyle();
  const editorStore = useEstimateEditorStore();
  
  const [ anchor, setAnchor ] = useState(null);
  const [ loaded, setLoaded ] = useState(false);
  
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const onTextChangeRef = useRef(onTextChange);
  const defaultValueRef = useRef(defaultValue);
  const selectionRef = useRef(0);
  const readOnlyRef = useRef(readOnly);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  
  const delayedDeltasRef = useRef([]);
  const delayedActionRef = useRef();
  
  const handleBlur = (reason) => {
    if ( !ref.current || !focused )
      return;
    onBlur && onBlur(reason);
  }
  
  const handleToolbarRef = useCallback((r) => {
    toolbarRef.current = r;
    setLoaded(Boolean(r));
  }, []);
  
  useLayoutEffect(() => {
    onTextChangeRef.current = onTextChange;
    defaultValueRef.current = defaultValue;
    readOnlyRef.current = readOnly;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
  });
  
  useEffect(() => {
    ref.current?.enable(!readOnly);
  }, [ref, readOnly]);
  
  useEffect(() => {
    if(!ref.current) return;
    if(defaultValue) {
      const diff = ref.current.getContents().diff(new Delta(defaultValue.ops.filter(o => o.insert)));
      if ( diff.ops.length )
        ref.current.updateContents(diff, "api");
    } else {
      ref.current.updateContents(new Delta(), "api")
    }
  }, [defaultValue]);
  
  useLayoutEffect(() => {
    
    const container = containerRef.current;
    const editorContainer = container.appendChild(
      container.ownerDocument.createElement('div'),
    );
    
    ref.current =  new Quill(editorContainer, {
      theme: null,
      placeholder,
      formats: WYSIWYG_FORMATS,
      modules: {
        clipboard: { matchVisual: false },
        history: { maxStack: 0, userOnly: true, },
        toolbar: toolbarRef.current,
      }
    });
    
    if(readOnly)
      ref.current.enable(!readOnly);
    
    if (defaultValueRef.current) {
      // const delta = quill.clipboard.convert({html: defaultValueRef.current})
      ref.current.setContents(defaultValueRef.current, "api")
      editorStore.setRenderedTextDescription(ref.current.root.innerHTML)
    }
    
    ref.current.root.addEventListener('paste', (ev) => {
      formatUrlOnPaste(ev, ref.current)
    }, true);
    
    ref.current.on(Quill.events.TEXT_CHANGE, (delta, oldDelta, source) => {
      
      if(readOnlyRef.current || source !== "user") return;
      const contentLength = ref.current.getLength() - 1;
      
      if(delta.ops.length === 2 && typeof delta.ops[1].insert === "string" ) {
        const linkDelta = autoFormatUrls(ref.current);
        if(linkDelta?.ops?.length && linkDelta?.ops?.length > 0)
          delta = delta.compose(linkDelta)
      }
      
      editorStore.setRenderedTextDescription(ref.current.root.innerHTML)
      
      delayedDeltasRef.current.push(delta);
      if(contentLength > MAX_TEXT_DESCRIPTION_LENGTH) {
        const deleteDelta = ref.current.deleteText(MAX_TEXT_DESCRIPTION_LENGTH, contentLength, "api");
        delayedDeltasRef.current.push(deleteDelta);
      }
      clearTimeout(delayedActionRef.current);
      
      const handleDelayedSave = () => {
        let length = ref.current.getLength();
        const content = ref.current.getContents();
        if(length === 1 && content.ops.length <= 1 && content.ops[0].insert && content.ops[0].insert === "\n")
          length = 0;
        
        onTextChangeRef.current
          ?.(delayedDeltasRef.current, length);
        delayedDeltasRef.current = [];
      };
      
      delayedActionRef.current = setTimeout(handleDelayedSave, changeDelay);
    });
    
    ref.current.on(Quill.events.EDITOR_CHANGE, (eventName, rangeOrDelta, oldRangeOrDelta) => {
      if (readOnlyRef.current || isEqual(rangeOrDelta, oldRangeOrDelta)) return;
      if (eventName === Quill.events.SELECTION_CHANGE && rangeOrDelta) {
        setAnchor(getQuillEditorToolPopupPosition(ref.current, containerRef.current, rangeOrDelta))
      }
      if (!oldRangeOrDelta && rangeOrDelta) {
        selectionRef.current = rangeOrDelta;
        onFocusRef.current?.();
      }
    });
    
    if(selectionRef.current) {
      /** @note: "user" allows mention module insert right after focus on "@" */
      ref.current.setSelection(selectionRef.current, "user");
      selectionRef.current = null;
    }
    
    return () => {
      setAnchor(null);
      ref.current = null;
      container.innerHTML = '';
    };
  }, [ref, loaded]);
  
  return <ClickAwayListener
    mouseEvent="onMouseDown"
    onClickAway={() => handleBlur("clickAway")}
  >
    <Grid
      item container
      translate="no"
      direction="column"
      className={classnames(
        classes.root,
        focused && classes.focused,
        hasError && classes.error,
        "ql-root w-full",
        className,
      )}
    >
      <div className={classnames("enable-undo", classes.editor)} ref={containerRef} />
      
      {
        anchor &&
        <PopMenu
          anchor={anchor}
          show={focused}
          placement="bottom-start"
          pinToViewport
          offset={40}
        >
          <QuillToolbar
            ref={handleToolbarRef}
            visible float
          />
        </PopMenu>
      }
    </Grid>
  </ClickAwayListener>;
});

DescriptionEditor.displayName = 'DescriptionEditor';
DescriptionEditor.propTypes = {
  value: shape({
    ops: arrayOf(shape({
      insert: string.isRequired,
      attributes: object
    })).isRequired,
  }),
  focused: bool,
  onFocus: func,
  onBlur: func,
  onTextChange: func.isRequired,
  placeholder: string,
  className: string,
  readOnly: bool,
  hasError: bool,
  changeDelay: number,
}